/**
 * P6 work-calendar helpers (ported from the P6_Linux p6-cal algorithm).
 *
 * Dates are naive project wall time: compare YYYY-MM-DD strings or local
 * dayjs/Date(y, m-1, d). Never Date.UTC / ISO-midnight, which shift holidays.
 *
 * Weeks are Monday–Sunday (HITO). Filter, Calendar, 3WLA, and resource graphs
 * all use Monday-start / Sunday week-ending.
 */

const OLE_EPOCH_Y = 1899;
const OLE_EPOCH_M = 12;
const OLE_EPOCH_D = 30;

export function parseYmd (ymd) {
	const parts = String(ymd || '').split('-');
	return {
		y: Number(parts[0]) || 0,
		m: Number(parts[1]) || 1,
		d: Number(parts[2]) || 1
	};
}

export function formatYmd (y, m, d) {
	const mm = m < 10 ? `0${m}` : String(m);
	const dd = d < 10 ? `0${d}` : String(d);
	return `${y}-${mm}-${dd}`;
}

export function ymdToLocalDate (ymd) {
	const { y, m, d } = parseYmd(ymd);
	return new Date(y, m - 1, d);
}

export function msToYmd (ms) {
	const n = Number(ms);
	if (!n) {
		return '';
	}
	const dt = new Date(n);
	return formatYmd(dt.getFullYear(), dt.getMonth() + 1, dt.getDate());
}

export function addDaysYmd (ymd, days) {
	const dt = ymdToLocalDate(ymd);
	dt.setDate(dt.getDate() + days);
	return formatYmd(dt.getFullYear(), dt.getMonth() + 1, dt.getDate());
}

export function weekdaySun0FromYmd (ymd) {
	return ymdToLocalDate(ymd).getDay();
}

export function oleSerialToYmd (serial) {
	const n = Number(serial);
	if (!Number.isFinite(n)) {
		return '';
	}
	const dt = new Date(OLE_EPOCH_Y, OLE_EPOCH_M - 1, OLE_EPOCH_D);
	dt.setDate(dt.getDate() + Math.round(n));
	return formatYmd(dt.getFullYear(), dt.getMonth() + 1, dt.getDate());
}

export function ymdToOleSerial (ymd) {
	const a = ymdToLocalDate(ymd);
	const b = new Date(OLE_EPOCH_Y, OLE_EPOCH_M - 1, OLE_EPOCH_D);
	return Math.round((a - b) / 86400000);
}

function normalizeClndrData (raw) {
	return String(raw || '')
		.replace(/\u007f/g, ' ')
		.replace(/[\r\n\t]+/g, ' ');
}

function parseHhMm (token) {
	const m = /(\d{1,2}):(\d{2})/.exec(String(token || ''));
	if (!m) {
		return 0;
	}
	return (Number(m[1]) * 60) + Number(m[2]);
}

export function parseShiftValue (value) {
	const parts = String(value || '').split('|');
	let start = null;
	let finish = null;
	for (let i = 0; i < parts.length - 1; i++) {
		if (parts[i] === 's') {
			start = parseHhMm(parts[i + 1]);
		} else if (parts[i] === 'f') {
			finish = parseHhMm(parts[i + 1]);
		}
	}
	if (start == null || finish == null) {
		return null;
	}
	if (start === 0 && finish === 0) {
		return { s: 0, f: 1440 };
	}
	if (finish <= start) {
		finish += 1440;
	}
	return { s: start, f: finish };
}

function parseRecords (str, indexRef) {
	const out = [];
	const s = str;
	let i = indexRef.i;
	const len = s.length;
	const skipWs = () => {
		while (i < len && s.charCodeAt(i) <= 32) {
			i++;
		}
	};
	while (true) {
		skipWs();
		if (i >= len || s[i] !== '(') {
			break;
		}
		i++;
		const pipes = s.indexOf('||', i);
		if (pipes < 0) {
			break;
		}
		i = pipes + 2;
		let name = '';
		while (i < len && s[i] !== '(') {
			name += s[i];
			i++;
		}
		if (i >= len || s[i] !== '(') {
			break;
		}
		i++;
		let value = '';
		while (i < len && s[i] !== ')') {
			value += s[i];
			i++;
		}
		if (i < len && s[i] === ')') {
			i++;
		}
		skipWs();
		const childRef = { i };
		let children = [];
		if (i < len && s[i] === '(') {
			i++;
			childRef.i = i;
			children = parseRecords(s, childRef);
			i = childRef.i;
			skipWs();
			if (i < len && s[i] === ')') {
				i++;
			}
		}
		skipWs();
		if (i < len && s[i] === ')') {
			i++;
		}
		out.push({ name: name.trim(), value, children });
	}
	indexRef.i = i;
	return out;
}

function findNamed (records, name) {
	for (let i = 0; i < records.length; i++) {
		if (records[i].name === name) {
			return records[i];
		}
		const nested = findNamed(records[i].children || [], name);
		if (nested) {
			return nested;
		}
	}
	return null;
}

function shiftsFromChildren (children) {
	const shifts = [];
	(children || []).forEach(child => {
		const parsed = parseShiftValue(child.value);
		if (parsed) {
			shifts.push(parsed);
		}
		(child.children || []).forEach(grand => {
			const inner = parseShiftValue(grand.value);
			if (inner) {
				shifts.push(inner);
			}
		});
	});
	return shifts;
}

function cloneWeek (week) {
	return (week || []).map(day => (day || []).map(shift => ({ s: shift.s, f: shift.f })));
}

export function fallbackWeek (dayHrCnt, weekHrCnt) {
	const hours = Number(dayHrCnt) > 0 ? Number(dayHrCnt) : 8;
	const weekHrs = Number(weekHrCnt) || 0;
	const start = 8 * 60;
	const finish = start + Math.round(hours * 60);
	const shift = [{ s: start, f: finish }];
	const seven = weekHrs >= 56 || (hours > 0 && weekHrs / hours >= 6.5);
	const week = [[], [], [], [], [], [], []];
	if (seven) {
		for (let d = 0; d < 7; d++) {
			week[d] = shift.map(s => ({ ...s }));
		}
	} else {
		for (let d = 1; d <= 5; d++) {
			week[d] = shift.map(s => ({ ...s }));
		}
	}
	return week;
}

export function parseP6Calendar (clndrData, meta) {
	const dayHrCnt = Number(meta && meta.dayHrCnt) || Number(meta && meta.day_hr_cnt) || 8;
	const weekHrCnt = Number(meta && meta.weekHrCnt) || Number(meta && meta.week_hr_cnt) || 40;
	const normalized = normalizeClndrData(clndrData);
	const records = parseRecords(normalized, { i: 0 });
	const daysNode = findNamed(records, 'DaysOfWeek');
	const exceptionsNode = findNamed(records, 'Exceptions');
	const week = [[], [], [], [], [], [], []];
	let hasWeek = false;
	if (daysNode && daysNode.children && daysNode.children.length) {
		daysNode.children.forEach(dayRec => {
			const p6Day = Number(dayRec.name);
			if (p6Day < 1 || p6Day > 7) {
				return;
			}
			hasWeek = true;
			week[p6Day - 1] = shiftsFromChildren(dayRec.children);
		});
	}
	const exceptions = {};
	if (exceptionsNode && exceptionsNode.children) {
		exceptionsNode.children.forEach(ex => {
			const dm = /d\|(\d+)/.exec(ex.value || '');
			if (!dm) {
				return;
			}
			const ymd = oleSerialToYmd(Number(dm[1]));
			if (!ymd) {
				return;
			}
			exceptions[ymd] = shiftsFromChildren(ex.children);
		});
	}
	return {
		week: hasWeek ? week : null,
		exceptions,
		dayHrCnt,
		weekHrCnt,
		hasWeek
	};
}

export function resolveCalendarRows (rows) {
	const byId = {};
	(rows || []).forEach(row => {
		if (!row) {
			return;
		}
		const id = String(row.clndr_id != null ? row.clndr_id : row.id);
		byId[id] = row;
	});
	const resolved = {};
	const walking = {};

	const resolveOne = id => {
		const key = String(id);
		if (resolved[key]) {
			return resolved[key];
		}
		const row = byId[key];
		if (!row) {
			return null;
		}
		if (walking[key]) {
			const parsed = parseP6Calendar(row.clndr_data, row);
			const week = parsed.hasWeek ? parsed.week : fallbackWeek(parsed.dayHrCnt, parsed.weekHrCnt);
			return compactFromParsed(row, week, parsed.exceptions, parsed.dayHrCnt, parsed.weekHrCnt);
		}
		walking[key] = true;
		const parsed = parseP6Calendar(row.clndr_data, row);
		let week = parsed.week;
		const baseId = row.base_clndr_id != null && row.base_clndr_id !== ''
			? String(row.base_clndr_id)
			: '';
		if ((!week || !parsed.hasWeek) && baseId && baseId !== key && byId[baseId]) {
			const base = resolveOne(baseId);
			week = base ? cloneWeek(base.week) : fallbackWeek(parsed.dayHrCnt, parsed.weekHrCnt);
		}
		if (!week || (!parsed.hasWeek && !baseId)) {
			week = parsed.hasWeek ? parsed.week : fallbackWeek(parsed.dayHrCnt, parsed.weekHrCnt);
		}
		const compact = compactFromParsed(row, week, parsed.exceptions, parsed.dayHrCnt, parsed.weekHrCnt);
		resolved[key] = compact;
		delete walking[key];
		return compact;
	};

	Object.keys(byId).forEach(id => resolveOne(id));
	return resolved;
}

function compactFromParsed (row, week, exceptions, dayHrCnt, weekHrCnt) {
	const id = String(row.clndr_id != null ? row.clndr_id : row.id);
	return {
		id,
		name: row.clndr_name || row.name || '',
		type: row.clndr_type || row.type || '',
		defaultFlag: row.default_flag || row.defaultFlag || 'N',
		dayHrCnt: dayHrCnt || 8,
		weekHrCnt: weekHrCnt || 40,
		week: cloneWeek(week),
		exceptions: exceptions || {}
	};
}

export function fallbackCalendar () {
	return {
		id: '',
		name: 'Mon-Fri 8h',
		type: 'CA_Base',
		defaultFlag: 'Y',
		dayHrCnt: 8,
		weekHrCnt: 40,
		week: fallbackWeek(8, 40),
		exceptions: {}
	};
}

export function resolveCalendar (clndrId, calendars, projectClndrId, defaultClndrId) {
	const map = calendars || {};
	const tryId = id => {
		if (id == null || id === '') {
			return null;
		}
		return map[String(id)] || null;
	};
	return tryId(clndrId)
		|| tryId(projectClndrId)
		|| tryId(defaultClndrId)
		|| Object.keys(map).map(k => map[k]).find(cal => cal && (cal.defaultFlag === 'Y' || cal.default_flag === 'Y'))
		|| fallbackCalendar();
}

export function shiftsOn (ymd, cal) {
	if (!cal || !ymd) {
		return [];
	}
	if (Object.prototype.hasOwnProperty.call(cal.exceptions || {}, ymd)) {
		return cal.exceptions[ymd] || [];
	}
	const dow = weekdaySun0FromYmd(ymd);
	return (cal.week && cal.week[dow]) || [];
}

export function isWorkDay (ymd, cal) {
	return shiftsOn(ymd, cal).length > 0;
}

export function workHoursOn (ymd, cal) {
	return shiftsOn(ymd, cal).reduce((sum, shift) => sum + Math.max(0, (shift.f - shift.s) / 60), 0);
}

export function workingWeekdayCount (cal) {
	const week = (cal && cal.week) || fallbackWeek(8, 40);
	let n = 0;
	for (let d = 0; d < 7; d++) {
		if (week[d] && week[d].length) {
			n++;
		}
	}
	return n || 5;
}

export function isHolidayException (ymd, cal) {
	if (!cal || !ymd) {
		return false;
	}
	const exceptions = cal.exceptions || {};
	if (!Object.prototype.hasOwnProperty.call(exceptions, ymd)) {
		return false;
	}
	return !(exceptions[ymd] && exceptions[ymd].length);
}

export function holidayYmds (cal) {
	const out = [];
	const exceptions = (cal && cal.exceptions) || {};
	Object.keys(exceptions).forEach(ymd => {
		if (!exceptions[ymd] || !exceptions[ymd].length) {
			out.push(ymd);
		}
	});
	out.sort();
	return out;
}

function firstShiftStartMin (shifts) {
	if (!shifts.length) {
		return 0;
	}
	let min = shifts[0].s;
	for (let i = 1; i < shifts.length; i++) {
		if (shifts[i].s < min) {
			min = shifts[i].s;
		}
	}
	return min;
}

function lastShiftEndMin (shifts) {
	if (!shifts.length) {
		return 0;
	}
	let max = shifts[0].f;
	for (let i = 1; i < shifts.length; i++) {
		if (shifts[i].f > max) {
			max = shifts[i].f;
		}
	}
	return max;
}

export function paintsDay (ymd, startMs, finishMs, cal) {
	const shifts = shiftsOn(ymd, cal);
	if (!shifts.length) {
		return false;
	}
	const dayStart = ymdToLocalDate(ymd).getTime();
	const first = dayStart + (firstShiftStartMin(shifts) * 60000);
	if (finishMs && finishMs <= first) {
		return false;
	}
	const last = dayStart + (lastShiftEndMin(shifts) * 60000);
	if (startMs && startMs >= last) {
		return false;
	}
	return true;
}

export function overlapWorkMinutes (ymd, startMs, finishMs, cal) {
	const shifts = shiftsOn(ymd, cal);
	if (!shifts.length) {
		return 0;
	}
	const dayStart = ymdToLocalDate(ymd).getTime();
	let mins = 0;
	shifts.forEach(shift => {
		const a = Math.max(Number(startMs) || 0, dayStart + (shift.s * 60000));
		const b = Math.min(Number(finishMs) || a, dayStart + (shift.f * 60000));
		if (b > a) {
			mins += (b - a) / 60000;
		}
	});
	return mins;
}

export function workMinutesBetween (startMs, finishMs, cal) {
	const start = Number(startMs) || 0;
	const finish = Number(finishMs) || start;
	if (!start) {
		return 0;
	}
	const days = workDaysInRange(start, finish, cal);
	return days.reduce((sum, ymd) => sum + overlapWorkMinutes(ymd, start, finish, cal), 0);
}

/**
 * Instant when percentComplete of *working* minutes between start and finish
 * has elapsed (activity calendar). Used so Gantt fill is one progress across
 * split work segments, not restarted on each piece.
 */
export function progressPointMs (startMs, finishMs, percentComplete, cal) {
	const start = Number(startMs) || 0;
	const finish = Number(finishMs) || start;
	const pct = Math.max(0, Math.min(100, Number(percentComplete) || 0));
	if (!start) {
		return 0;
	}
	if (pct <= 0) {
		return start;
	}
	if (pct >= 100) {
		return finish;
	}
	const total = workMinutesBetween(start, finish, cal);
	if (total <= 0) {
		return start + ((finish - start) * (pct / 100));
	}
	const target = total * (pct / 100);
	const days = workDaysInRange(start, finish, cal);
	let acc = 0;
	for (let i = 0; i < days.length; i++) {
		const ymd = days[i];
		const mins = overlapWorkMinutes(ymd, start, finish, cal);
		if (acc + mins >= target) {
			const need = target - acc;
			const shifts = shiftsOn(ymd, cal);
			const dayStart = ymdToLocalDate(ymd).getTime();
			let walked = 0;
			for (let s = 0; s < shifts.length; s++) {
				const a = Math.max(start, dayStart + (shifts[s].s * 60000));
				const b = Math.min(finish, dayStart + (shifts[s].f * 60000));
				if (b <= a) {
					continue;
				}
				const piece = (b - a) / 60000;
				if (walked + piece >= need) {
					return a + ((need - walked) * 60000);
				}
				walked += piece;
			}
			return dayStart + (lastShiftEndMin(shifts) * 60000);
		}
		acc += mins;
	}
	return finish;
}

/** Bootstrap ProgressBar `now` (0–100) for one work segment given a global progress instant. */
export function segmentPercentNow (segStart, segFinish, progressMs) {
	const a = Number(segStart) || 0;
	const b = Number(segFinish) || a;
	const p = Number(progressMs) || 0;
	if (!a || b <= a) {
		return 0;
	}
	if (p <= a) {
		return 0;
	}
	if (p >= b) {
		return 100;
	}
	return Math.max(0, Math.min(100, ((p - a) / (b - a)) * 100));
}

export function workDaysInRange (startMs, finishMs, cal) {
	const startYmd = msToYmd(startMs);
	const endYmd = msToYmd(finishMs || startMs);
	if (!startYmd) {
		return [];
	}
	const days = [];
	let ymd = startYmd;
	let guard = 0;
	while (ymd && ymd <= endYmd && guard < 4000) {
		if (paintsDay(ymd, startMs, finishMs || startMs, cal)) {
			days.push(ymd);
		}
		ymd = addDaysYmd(ymd, 1);
		guard++;
	}
	return days;
}

export function splitIntoWorkSegments (startMs, finishMs, cal) {
	const start = Number(startMs) || 0;
	const finish = Number(finishMs) || start;
	if (!start) {
		return [];
	}
	const days = workDaysInRange(start, finish, cal);
	if (!days.length) {
		return [];
	}
	const segments = [];
	let runStart = null;
	let prev = null;
	const flush = endYmd => {
		if (!runStart) {
			return;
		}
		const segStart = Math.max(start, ymdToLocalDate(runStart).getTime());
		const endDay = ymdToLocalDate(endYmd);
		const endShifts = shiftsOn(endYmd, cal);
		const lastMin = lastShiftEndMin(endShifts);
		const segFinish = Math.min(finish, endDay.getTime() + (lastMin * 60000));
		segments.push({ start: segStart, finish: segFinish });
		runStart = null;
	};
	days.forEach(ymd => {
		if (prev && addDaysYmd(prev, 1) === ymd) {
			prev = ymd;
			return;
		}
		if (prev) {
			flush(prev);
		}
		runStart = ymd;
		prev = ymd;
	});
	if (prev) {
		flush(prev);
	}
	return segments;
}

/**
 * Consecutive non-working days as grey Gantt segments (weekends and holidays).
 * Pass `{ includeNonWorking: false }` to skip shading entirely (continuous bars).
 */
export function splitIntoNonWorkSegments (startMs, finishMs, cal, options) {
	if (options && options.includeNonWorking === false) {
		return [];
	}
	const start = Number(startMs) || 0;
	const finish = Number(finishMs) || start;
	if (!start || finish < start) {
		return [];
	}
	const startYmd = msToYmd(start);
	const endYmd = msToYmd(finish);
	if (!startYmd) {
		return [];
	}
	const days = [];
	let ymd = startYmd;
	let guard = 0;
	while (ymd && ymd <= endYmd && guard < 4000) {
		if (!isWorkDay(ymd, cal)) {
			days.push(ymd);
		}
		ymd = addDaysYmd(ymd, 1);
		guard++;
	}
	if (!days.length) {
		return [];
	}
	const segments = [];
	let runStart = null;
	let prev = null;
	const flush = endYmd => {
		if (!runStart) {
			return;
		}
		const segStart = Math.max(start, ymdToLocalDate(runStart).getTime());
		const segFinish = Math.min(finish, ymdToLocalDate(endYmd).getTime() + 86400000);
		if (segFinish > segStart) {
			segments.push({
				start: segStart,
				finish: segFinish
			});
		}
		runStart = null;
	};
	days.forEach(day => {
		if (prev && addDaysYmd(prev, 1) === day) {
			prev = day;
			return;
		}
		if (prev) {
			flush(prev);
		}
		runStart = day;
		prev = day;
	});
	if (prev) {
		flush(prev);
	}
	return segments;
}

export function defaultCalendarId (calendars) {
	const map = calendars || {};
	const ids = Object.keys(map);
	for (let i = 0; i < ids.length; i++) {
		const cal = map[ids[i]];
		if (cal && (cal.defaultFlag === 'Y' || cal.default_flag === 'Y')) {
			return ids[i];
		}
	}
	return ids[0] || '';
}

export function projectCalendar (dataset) {
	const calendars = (dataset && dataset.calendars) || {};
	const projects = dataset && dataset.projects && !Array.isArray(dataset.projects)
		? dataset.projects
		: {};
	const firstProj = Object.keys(projects).map(k => projects[k])[0] || {};
	return resolveCalendar(
		null,
		calendars,
		firstProj.clndrId != null ? firstProj.clndrId : firstProj.clndr_id,
		defaultCalendarId(calendars)
	);
}

export function calendarForActivity (activity, dataset) {
	const calendars = (dataset && dataset.calendars) || {};
	const projects = dataset && dataset.projects && !Array.isArray(dataset.projects)
		? dataset.projects
		: null;
	const proj = (projects && activity && activity.projId && projects[activity.projId]) || {};
	return resolveCalendar(
		activity && (activity.clndrId != null ? activity.clndrId : activity.clndr_id),
		calendars,
		proj.clndrId != null ? proj.clndrId : proj.clndr_id,
		defaultCalendarId(calendars)
	);
}

/** ThirtyThird.ShowGanttHolidays — default on. Layouts Gantt shade + bar splits only. */
export function showGanttNonWorkingTime (dataset) {
	if (!dataset) {
		return true;
	}
	return dataset.ShowGanttHolidays !== false && dataset.ShowGanttHolidays !== 0;
}
